import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { AdmintoolService } from 'src/app/services/admin-tool/admintool.service';
import * as XLSX from 'xlsx';

@Component({
  selector: 'app-admintool',
  standalone: true,
  imports: [CommonModule, TranslateModule],
  templateUrl: './admintool.component.html',
  styleUrl: './admintool.component.scss'
})
export class AdmintoolComponent implements OnInit {

  data: any = null;

  readonly regions: string[] = [
    'Central East',
    'Central West',
    'East',
    'North',
    'West'
  ];

  private readonly businessSizeCategories: string[] = [
    'Owner only',
    '1 - 5',
    '6 - 19',
    '20 - 49',
    '50 - 99',
    '100+'
  ];

  constructor(public admintoolService: AdmintoolService, public translate: TranslateService) { }

  ngOnInit(): void {
    this.admintoolService.getDashboard().subscribe({
      next: (res: any) => {
        this.data = res;

        console.log('FULL RESPONSE:', res);
        console.log('RESPONSE KEYS:', Object.keys(res));
        console.log('DESIGNATED SUBSTANCES:', res?.businessesByDesignatedSubstance);

      },
      error: (err) => {
        console.error('Dashboard error:', err);
      }
    });
  }

  private normalizeBusinessSize(size: string): string {

    if (!size) {
      return '';
    }

    const cleaned = size
      .toString()
      .replace(/\s+/g, '')
      .toLowerCase();

    switch (cleaned) {

      case 'owneronly':
        return 'Owner only';

      case '1to5':
      case '1-5':
        return '1 - 5';

      case '6to19':
      case '6-19':
        return '6 - 19';

      case '20to49':
      case '20-49':
        return '20 - 49';

      case '50to99':
      case '50-99':
        return '50 - 99';

      case '100+':
      case '100plus':
        return '100+';

      default:
        return size;
    }
  }

  private businessSizeOrder(size: string): number {

    const order: { [key: string]: number } = {
      'Owner only': 1,
      '1 - 5': 2,
      '6 - 19': 3,
      '20 - 49': 4,
      '50 - 99': 5,
      '100+': 6
    };

    return order[size] || 999;
  }

  private regionPropMap(region: string): string {

    if (!region) {
      return '';
    }

    const normalizedRegion = region
      .toString()
      .replace(/\s+/g, '')
      .trim()
      .toLowerCase();

    switch (normalizedRegion) {

      case 'centraleast':
        return 'centralEast';

      case 'centralwest':
        return 'centralWest';

      case 'east':
        return 'east';

      case 'north':
        return 'north';

      case 'west':
        return 'west';

      default:
        return '';
    }
  }

  get usersBySector(): any[] {
    return this.data?.usersBySector || [];
  }

  get usersByHazard(): any[] {
    return this.data?.usersByHazard || [];
  }

  get businessesWithCompletedPlansByHazard(): any[] {
    return this.data?.businessesWithCompletedPlansByHazard || [];
  }

  get totalCompletedHazardsList(): any[] {
    return this.data?.totalCompletedHazardsList || [];
  }

  get workerTypesRows(): any[] {
    const rows = this.data?.usersByWorkerType || [];

    return rows.map((w: any) => ({
      ...w,
      totalUsers: this.getSectorUserCount(w.sector)
    }));
  }

  get workerTypesTotals(): any {
    const rows = this.workerTypesRows;

    return {
      partTimeCasualSeasonal: rows.reduce((sum: number, r: any) => sum + (r.partTimeCasualSeasonal || 0), 0),
      under25Workers: rows.reduce((sum: number, r: any) => sum + (r.under25Workers || 0), 0),
      foreignWorkers: rows.reduce((sum: number, r: any) => sum + (r.foreignWorkers || 0), 0),
      totalUsers: this.data?.registeredUsers || 0
    };
  }

  getSectorUserCount(sector: string): number {
    const sectorRow = this.usersBySector.find((s: any) => s.sector === sector);
    return sectorRow ? sectorRow.count : 0;
  }

  get businessesByDesignatedSubstance(): any[] {
    return this.data?.businessesByDesignatedSubstance || [];
  }

  get regionMatrixRows(): any[] {

    const source =
      this.data?.usersByRegionAndBusinessSize || [];


    // -----------------------------------------------------
    // Get sectors primarily from UsersBySector.
    //
    // This is important because a sector may have businesses
    // but no Region data at all.
    // -----------------------------------------------------

    const sectorSet = new Set<string>();


    (this.data?.usersBySector || []).forEach((item: any) => {

      const sector =
        item.sector
          ? item.sector.toString().trim()
          : 'No Sector';

      sectorSet.add(sector);
    });


    // Also include any sectors found in Region data
    source.forEach((item: any) => {

      const sector =
        item.sector
          ? item.sector.toString().trim()
          : 'No Sector';

      sectorSet.add(sector);
    });


    const sectors =
      Array.from(sectorSet);


    const grouped: { [key: string]: any } = {};


    // =====================================================
    // STEP 1
    // CREATE ALL 6 BUSINESS SIZE ROWS FOR EVERY SECTOR
    // =====================================================

    sectors.forEach((sector: string) => {

      this.businessSizeCategories.forEach(
        (businessSize: string) => {

          const key =
            `${sector}__${businessSize}`;


          grouped[key] = {

            sector: sector,

            businessSize: businessSize,

            centralEast: 0,

            centralWest: 0,

            east: 0,

            north: 0,

            west: 0,

            total: 0
          };

        }
      );

    });


    // =====================================================
    // STEP 2
    // PUT ACTUAL DATABASE VALUES INTO THE MATRIX
    // =====================================================

    source.forEach((item: any) => {

      const sector =
        item.sector
          ? item.sector.toString().trim()
          : 'No Sector';


      const businessSize =
        this.normalizeBusinessSize(
          item.businessSize || ''
        );


      const region =
        item.region
          ? item.region.toString().trim()
          : '';


      const businessCount =
        Number(item.businessCount) || 0;


      const key =
        `${sector}__${businessSize}`;


      // Safety fallback in case database contains
      // a business-size value outside our 6 categories.
      if (!grouped[key]) {

        grouped[key] = {

          sector: sector,

          businessSize: businessSize,

          centralEast: 0,

          centralWest: 0,

          east: 0,

          north: 0,

          west: 0,

          total: 0
        };
      }


      const regionProperty =
        this.regionPropMap(region);


      if (regionProperty) {

        grouped[key][regionProperty] +=
          businessCount;
      }


      grouped[key].total +=
        businessCount;

    });


    // =====================================================
    // STEP 3
    // SORT BY SECTOR THEN BUSINESS SIZE
    // =====================================================

    return Object
      .values(grouped)

      .sort((a: any, b: any) => {

        const sectorCompare =
          a.sector.localeCompare(
            b.sector
          );


        if (sectorCompare !== 0) {

          return sectorCompare;
        }


        return (
          this.businessSizeOrder(
            a.businessSize
          )
          -
          this.businessSizeOrder(
            b.businessSize
          )
        );

      });
  }

  // =====================================================
  // DOWNLOAD EXCEL - ADMIN DASHBOARD
  // =====================================================

  downloadExcel(): void {

    if (!this.data) {
      console.error('Dashboard data is not available.');
      return;
    }

    const workbook: XLSX.WorkBook = XLSX.utils.book_new();

    // =====================================================
    // HELPER FUNCTION - CREATE EXCEL SHEET
    // =====================================================

    const addSheet = (
      sheetName: string,
      sheetData: any[][],
      columnWidths: number[]
    ): void => {

      const worksheet = XLSX.utils.aoa_to_sheet(sheetData);

      worksheet['!cols'] = columnWidths.map(width => ({
        wch: width
      }));

      XLSX.utils.book_append_sheet(
        workbook,
        worksheet,
        sheetName
      );
    };


    // =====================================================
    // SHEET 1 - DASHBOARD SUMMARY
    // =====================================================

    const summaryData = [
      ['Admin Dashboard Summary'],
      [],
      ['Metric', 'Count'],

      [
        'Total Registered Users',
        this.data.registeredUsers || 0
      ],

      [
        'Total Safety Plans Created',
        this.data.safetyPlansCreated || 0
      ],

      [
        'Total Safety Plans Completed',
        this.data.completedSafetyPlans || 0
      ],

      [
        'Businesses with Completed Safety Plans',
        this.data.totalBusinessesWithCompletedSafetyPlans || 0
      ]
    ];

    addSheet(
      'Summary',
      summaryData,
      [45, 18]
    );


    // =====================================================
    // SHEET 2 - BUSINESS USERS BY SECTOR
    // =====================================================

    const sectorData = [
      ['Business Users by Sector'],
      [],
      [
        'Sector',
        'Number Of Businesses',
        '% of Total Businesses'
      ],

      ...this.usersBySector.map((s: any) => [
        s.sector,
        s.count || 0,
        `${s.percentage ?? 0}%`
      ]),

      [],
      [
        'Total',
        this.data.registeredUsers || 0,
        '100%'
      ]
    ];

    addSheet(
      'Users by Sector',
      sectorData,
      [40, 28, 28]
    );


    // =====================================================
    // SHEET 3 - WORKER TYPES BY SECTOR
    // =====================================================

    const workerTypeData = [
      ['Worker Types by Sector for all Registered Users'],
      [],
      [
        'Sector',
        'Part-time / Casual / Seasonal',
        'Youth (Under 25)',
        'Contract Foreign Workers'
      ],

      ...this.workerTypesRows.map((w: any) => [
        w.sector,
        w.partTimeCasualSeasonal || 0,
        w.under25Workers || 0,
        w.foreignWorkers || 0
      ]),

      [],
      [
        'Total',
        this.workerTypesTotals.partTimeCasualSeasonal,
        this.workerTypesTotals.under25Workers,
        this.workerTypesTotals.foreignWorkers
      ]
    ];

    addSheet(
      'Worker Types',
      workerTypeData,
      [40, 35, 25, 32]
    );


    // =====================================================
    // SHEET 4 - BUSINESS SIZE AND REGIONS
    // =====================================================

    const businessRegionData = [
      [
        'Business Size and Number of Locations by Region for All Registered Users'
      ],
      [],
      [
        'Sector',
        'Business Size',
        'Central East',
        'Central West',
        'East',
        'North',
        'West',
        'Total'
      ],

      ...this.regionMatrixRows.map((r: any) => [
        r.sector,
        r.businessSize,
        r.centralEast || 0,
        r.centralWest || 0,
        r.east || 0,
        r.north || 0,
        r.west || 0,
        r.total || 0
      ])
    ];

    addSheet(
      'Business Size Region',
      businessRegionData,
      [40, 20, 18, 18, 15, 15, 15, 15]
    );


    // =====================================================
    // SHEET 5 - BUSINESSES WITH COMPLETED SAFETY PLANS
    // BY HAZARD
    // =====================================================

    const completedPlansData = [
      [
        'Businesses with Completed Safety Plans by Hazard'
      ],
      [],
      [
        'Hazard',
        'Hazard Type',
        'Total Businesses with Completed Safety Plans'
      ],

      ...this.businessesWithCompletedPlansByHazard.map(
        (h: any) => [
          h.hazard,
          h.typeOfHazard,
          h.count || 0
        ]
      )
    ];

    addSheet(
      'Completed Plans by Hazard',
      completedPlansData,
      [50, 30, 55]
    );


    // =====================================================
    // SHEET 6 - DESIGNATED SUBSTANCES
    // =====================================================

    const designatedSubstanceData = [
      [
        'Businesses and their selected Designated Substances'
      ],
      [],
      [
        'Designated Substance',
        'Total Businesses who selected these Designated Substances in their profile page'
      ],

      ...this.businessesByDesignatedSubstance.map(
        (ds: any) => [
          ds.designatedSubstance,
          ds.businessCount || 0
        ]
      )
    ];

    addSheet(
      'Designated Substances',
      designatedSubstanceData,
      [45, 85]
    );


    // =====================================================
    // SHEET 7 - BUSINESSES AND THEIR COMPLETED HAZARDS
    // =====================================================

    const completedHazardsData = [
      [
        'Businesses and their completed Hazards'
      ],
      [],
      [
        'Hazard',
        'Hazard Type',
        'Total Businesses who completed these hazards in their hazard card page'
      ],

      ...this.totalCompletedHazardsList.map(
        (h: any) => [
          h.hazard,
          h.typeOfHazard,
          h.count || 0
        ]
      )
    ];

    addSheet(
      'Completed Hazards',
      completedHazardsData,
      [50, 30, 85]
    );


    // =====================================================
    // DOWNLOAD EXCEL FILE
    // =====================================================

    const today = new Date();

    const dateString =
      `${today.getFullYear()}-` +
      `${String(today.getMonth() + 1).padStart(2, '0')}-` +
      `${String(today.getDate()).padStart(2, '0')}`;

    const fileName =
      `SafetyPlanner_Admin_Dashboard_${dateString}.xlsx`;

    XLSX.writeFile(workbook, fileName);

    console.log(
      'Admin Dashboard Excel downloaded successfully:',
      fileName
    );
  }
}